import React, { useContext, useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'
import { useSelector } from 'react-redux'

import { Card } from '@/components/cards/Card'
import { CHART } from '@/components/charts/chartPalette'
import { EntityClickContext } from '../helpers/dashboardContexts'
import { buildDashboardParams, useDashboardOrgId } from '../helpers/dashboardParams'
import { ScoredCallsBrowser } from './ScoredCallsBrowser'
import { ChartCard } from '../components/ChartCard'
import {
  EntityDrawerShell,
  EntityDrawerStats,
  buildDrawerScopeFilters,
  buildScorecardNameMap,
  buildScoreTrendOption,
  getScorecardLabel,
  mapFailingCriteriaRows,
  useDrawerAggregates,
} from './entityDrawer.helpers'
import { escapeHtml } from '../helpers/format'

export const ScorecardDetailDrawer = ({ scorecard, onClose }) => {
  const clickEntity = useContext(EntityClickContext)
  const filters = useSelector((state) => state.qaCopilot.filters)
  const allScorecards = useSelector((state) => state.qaCopilot.data.scorecards)
  const orgId = useDashboardOrgId()
  const sid = scorecard?.scorecard_config_sid || scorecard?.sid
  const scorecardNameById = useMemo(() => buildScorecardNameMap(allScorecards), [allScorecards])
  // Payload's own name first, else redux by sid (see getScorecardLabel).
  const displayName = getScorecardLabel(scorecard, scorecardNameById)

  // Pin to THIS scorecard's sid regardless of the dashboard filter.
  const params =
    sid && orgId
      ? (() => {
          const pinnedParams = buildDashboardParams({ ...filters, scorecards: null }, orgId)
          pinnedParams.append('scorecard_config_sids', sid)
          return pinnedParams.toString()
        })()
      : null
  const {
    data: [byDay, criteria],
    loading,
    error,
  } = useDrawerAggregates(params)

  const trendRows = useMemo(() => byDay?.aggregates || [], [byDay])
  const callsByDay = useMemo(() => byDay?.calls || [], [byDay])

  // Headline tiles — totals + call-weighted avg across the window.
  const totalCalls = useMemo(
    () => callsByDay.reduce((acc, row) => acc + (parseInt(row.y, 10) || 0), 0),
    [callsByDay]
  )
  const avgScore = useMemo(() => {
    if (isEmpty(trendRows)) {
      return null
    }
    let weightedSum = 0
    let totalWeight = 0
    trendRows.forEach((row, index) => {
      const score = parseFloat(row['QA Score']) || 0
      const weight = parseInt(callsByDay[index]?.y, 10) || 0
      weightedSum += score * weight
      totalWeight += weight
    })
    if (totalWeight === 0) {
      return null
    }
    return weightedSum / totalWeight
  }, [trendRows, callsByDay])
  const criteriaFlagged = (criteria || []).length

  const trendOption = useMemo(() => buildScoreTrendOption(trendRows), [trendRows])

  // Threshold-tier coloring mirrors `calculateGrade` in
  // views/Calls/components/helpers.js; fall back to 70/80/90.
  const tiers = useMemo(
    () => ({
      improvement: Number(scorecard?.improvement_threshold) || 70,
      meets: Number(scorecard?.meets_threshold) || 80,
      exceeds: Number(scorecard?.exceeds_threshold) || 90,
    }),
    [scorecard?.improvement_threshold, scorecard?.meets_threshold, scorecard?.exceeds_threshold]
  )

  const criteriaRows = useMemo(() => mapFailingCriteriaRows(criteria, 10), [criteria])

  const criteriaOption = useMemo(() => {
    // Chart shows % failing; invert to pass_rate for the tier lookup.
    const colorForFailingPct = (failingPct) => {
      const passRate = 100 - failingPct
      if (passRate >= tiers.exceeds) {
        return CHART.SUCCESS
      }
      if (passRate >= tiers.meets) {
        return CHART.WARNING
      }
      if (passRate >= tiers.improvement) {
        return CHART.CAUTION
      }
      return CHART.CRITICAL
    }
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (tooltipParams) => {
          const point = tooltipParams[0]
          const passRate = 100 - (point.value || 0)
          const cta = clickEntity ? '<br/><em>Click to drill into this criterion</em>' : ''
          return `${escapeHtml(point.name)}<br/>${point.value}% failing<br/>${passRate.toFixed(0)}% passing${cta}`
        },
      },
      grid: { left: 130, right: 24, top: 16, bottom: 16 },
      xAxis: { type: 'value', name: '% Failing', max: 100 },
      yAxis: {
        type: 'category',
        data: criteriaRows.map((row) => row.name),
        inverse: true,
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          cursor: clickEntity ? 'pointer' : 'default',
          data: criteriaRows.map((row) => ({
            value: row.value,
            itemStyle: { color: colorForFailingPct(row.value) },
            // Drill payload for the criterion drawer. This feed already
            // carries top_infractors, so Top Agents populates immediately.
            criterion: {
              criteria_id: row.row?.criteria_id,
              scorecard_config_sid: row.row?.scorecard_config_sid || sid,
              name: row.name,
              top_infractors: row.row?.top_infractors,
            },
          })),
        },
      ],
    }
  }, [criteriaRows, tiers, clickEntity, sid])

  // Clicking a failing-criterion bar opens the criterion drawer.
  const criteriaEvents = useMemo(
    () => ({
      click: (event) => {
        const payload = event?.data?.criterion
        if (payload?.criteria_id && clickEntity) {
          clickEntity({ type: 'criterion', payload })
        }
      },
    }),
    [clickEntity]
  )

  if (!scorecard) {
    return null
  }

  return (
    <EntityDrawerShell
      title={displayName}
      typeLabel="Scorecard"
      subtitle={
        scorecard.type && (
          <span>
            {' · '}
            {scorecard.type}
          </span>
        )
      }
      onClose={onClose}
    >
      <EntityDrawerStats
        stats={[
          { label: 'Calls', value: totalCalls.toLocaleString() },
          { label: 'Avg Score', value: !isNil(avgScore) ? `${avgScore.toFixed(0)}%` : '—' },
          { label: 'Days', value: trendRows.length.toLocaleString() },
          { label: 'Flagged Criteria', value: criteriaFlagged.toLocaleString() },
        ]}
      />

      <ChartCard
        title="Score Over Time"
        option={trendOption}
        disabled={!sid}
        disabledMessage="No scorecard id on the click payload. Trend unavailable."
        loading={loading}
        error={error}
        isEmpty={isEmpty(trendRows)}
        errorMessage="Couldn’t load this scorecard’s score history."
        emptyMessage="No scored calls for this scorecard in the current window."
      />

      <ChartCard
        title="Criteria Performance"
        option={criteriaOption}
        events={criteriaEvents}
        height={300}
        loading={loading}
        error={error}
        isEmpty={isEmpty(criteriaRows)}
        errorMessage="Couldn’t load criteria performance for this scorecard."
        emptyMessage="No failing criteria for this scorecard in the current window."
      />

      <Card title="Scored calls">
        <ScoredCallsBrowser
          // Pin to THIS scorecard regardless of the dashboard's filter.
          scopeFilters={buildDrawerScopeFilters(filters, { scorecards: [{ value: sid }] })}
          orgId={orgId}
        />
      </Card>
    </EntityDrawerShell>
  )
}
